import { createListenerMiddleware } from '@reduxjs/toolkit'

import { trackEvent } from '@/utils/analyticsUtil'
import { isNonEmptyString } from '@/utils/commonUtil'
import { formatFullNumber } from '@/utils/formatUtil'

import { type ReduxRootState } from '../store'
import { toastActions } from '../toast'

import { clipboardActions } from './slice'

export const clipboardListener = createListenerMiddleware()

const getRecNames = (cards: { name?: string }[]) =>
  cards
    .slice(0, 4)
    .map((r) => r.name)
    .filter(isNonEmptyString)
    .join(', ')

const describeRecs = (cards: { name?: string }[]) => {
  if (cards.length > 3) {
    return (
      <strong>
        {formatFullNumber(cards.length)} card{cards.length > 1 && <span>s</span>}
      </strong>
    )
  }

  return <strong>{getRecNames(cards)}</strong>
}

clipboardListener.startListening({
  actionCreator: clipboardActions.add,
  effect: ({ payload }, { dispatch, getOriginalState }) => {
    const before = new Set((getOriginalState() as ReduxRootState).clipboard.cards.map(({ cardId }) => cardId))
    const added = payload.filter(({ cardId }) => !before.has(cardId))
    if (added.length < 1) return

    dispatch(toastActions.add(<p>Added {describeRecs(added)} to your clipboard</p>))
    trackEvent({ action: 'clipboard_add_card', category: 'clipboard', label: getRecNames(added) })
  },
})

clipboardListener.startListening({
  actionCreator: clipboardActions.remove,
  effect: ({ payload }, { dispatch, getOriginalState }) => {
    const before = new Set((getOriginalState() as ReduxRootState).clipboard.cards.map(({ cardId }) => cardId))
    const removed = payload.filter(({ cardId }) => before.has(cardId))
    if (removed.length < 1) return

    dispatch(toastActions.add(<p>Removed {describeRecs(removed)} from your clipboard</p>))
  },
})

clipboardListener.startListening({
  actionCreator: clipboardActions.promote,
  effect: ({ payload: { cardMap } }, { dispatch, getOriginalState, getState }) => {
    const before = new Set(
      (getOriginalState() as ReduxRootState).clipboard.cards
        .filter(({ isCommander }) => isCommander)
        .map(({ cardId }) => cardId),
    )
    const promoted = (getState() as ReduxRootState).clipboard.cards
      .filter(({ cardId, isCommander }) => isCommander && !before.has(cardId))
      .map(({ cardId }) => ({ cardId, name: cardMap[cardId]?.name }))
    if (promoted.length < 1) return

    dispatch(toastActions.add(<p>Promoted {describeRecs(promoted)} to your clipboard&#39;s commander</p>))
  },
})

clipboardListener.startListening({
  actionCreator: clipboardActions.demote,
  effect: ({ payload }, { dispatch, getOriginalState }) => {
    const wasInClipboard = (getOriginalState() as ReduxRootState).clipboard.cards.some(
      ({ cardId }) => cardId === payload.cardId,
    )
    if (!wasInClipboard) return

    dispatch(toastActions.add(<p>Demoted {describeRecs([payload])} as your clipboard&#39;s commander</p>))
  },
})

clipboardListener.startListening({
  actionCreator: clipboardActions.addPending,
  effect: (_, { dispatch, getOriginalState, getState }) => {
    const before = (getOriginalState() as ReduxRootState).clipboard.cardsPending.length
    const after = (getState() as ReduxRootState).clipboard.cardsPending.length
    const added = after - before

    dispatch(
      toastActions.add(
        added > 0 ? (
          <p>
            Added <strong>{added}</strong> rec{added > 1 && <span>s</span>} <span>to your clipboard</span>
          </p>
        ) : (
          <p>No new recs could be found</p>
        ),
      ),
    )
    trackEvent({ action: 'clipboard_add_recs', category: 'clipboard', label: String(added) })
  },
})

clipboardListener.startListening({
  actionCreator: clipboardActions.clear,
  effect: (_, { dispatch }) => {
    dispatch(toastActions.add(<p>Cleared your clipboard</p>))
  },
})

clipboardListener.startListening({
  actionCreator: clipboardActions.clearPending,
  effect: (_, { dispatch }) => {
    dispatch(toastActions.add(<p>Cleared all recs from your clipboard</p>))
  },
})
